import { useMemo } from 'react';
import { Server, HardDrive, Cpu, Activity, Zap, GripVertical } from 'lucide-react';
import { DatacenterNode, FormFactor, NodeType } from '../../types/configurator';
import { clsx } from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

interface RackViewProps {
  formFactor: FormFactor;
  nodes: DatacenterNode[];
  onNodeClick?: (nodeId: string) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  selectedNodeId?: string;
}

const NODE_COLORS: Record<NodeType, { bg: string; border: string; text: string }> = {
  gateway: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-700' },
  compute: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700' },
  storage: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700' },
  daq: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700' },
};

const NODE_ICONS: Record<NodeType, typeof Server> = {
  gateway: Server,
  compute: Cpu,
  storage: HardDrive,
  daq: Activity,
};

// Height per U in pixels for visualization
const PIXELS_PER_U = 24;

interface RackSlot {
  startU: number;
  endU: number;
  node: DatacenterNode | null;
}

interface DraggableRackNodeProps {
  node: DatacenterNode;
  slotHeight: number;
  isSelected: boolean;
  onNodeClick?: (nodeId: string) => void;
}

function DraggableRackNode({ node, slotHeight, isSelected, onNodeClick }: DraggableRackNodeProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const Icon = NODE_ICONS[node.type];
  const colors = NODE_COLORS[node.type];
  const customColor = node.specs.panelColor;

  return (
    <div ref={setNodeRef} style={style} className="flex">
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex items-center justify-center rounded-l border-2 border-r-0 border-gray-500 bg-gray-600 px-1 text-gray-400 hover:bg-gray-500 hover:text-gray-200 active:cursor-grabbing"
        style={{ height: slotHeight }}
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Node content */}
      <button
        onClick={() => onNodeClick?.(node.id)}
        className={clsx(
          'flex flex-1 items-center gap-2 rounded-r border-2 border-l-0 px-3 transition-all',
          !customColor && colors.bg,
          !customColor && colors.border,
          isSelected && 'ring-2 ring-hermit-500 ring-offset-1',
          'hover:opacity-90 active:scale-[0.99]'
        )}
        style={{
          height: slotHeight,
          ...(customColor && {
            backgroundColor: customColor,
            borderColor: customColor,
          }),
        }}
      >
        <Icon className={clsx('h-4 w-4', customColor ? 'text-white' : colors.text)} />
        <div className="flex-1 overflow-hidden text-left">
          <div className={clsx('truncate text-sm font-medium', customColor ? 'text-white' : colors.text)}>
            {node.hostname}
          </div>
          {slotHeight >= 40 && (
            <div className={clsx('truncate text-xs', customColor ? 'text-gray-200' : 'text-gray-600')}>
              {node.specs.model || node.name}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className={clsx('text-xs font-medium', customColor ? 'text-white' : colors.text)}>
            {node.specs.rackUnits || 1}U
          </div>
          {slotHeight >= 40 && (
            <div className={clsx('text-xs', customColor ? 'text-gray-200' : 'text-gray-500')}>
              {node.specs.powerWatts + (node.specs.gpu?.tdpWatts || 0)}W
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

export function RackView({ formFactor, nodes, onNodeClick, onReorder, selectedNodeId }: RackViewProps) {
  // Determine total rack units based on form factor
  const totalUnits = formFactor === 'rack-42' ? 42 : 18;

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorder) {
      const oldIndex = nodes.findIndex((n) => n.id === active.id);
      const newIndex = nodes.findIndex((n) => n.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  // Calculate rack slots from nodes (preserve user order, don't auto-sort)
  const { slots, usedUnits, totalPower, maxPower } = useMemo(() => {
    const result: RackSlot[] = [];
    let currentU = 1;
    let usedU = 0;
    let power = 0;

    for (const node of nodes) {
      const nodeU = node.specs.rackUnits || 1;
      if (currentU + nodeU - 1 <= totalUnits) {
        result.push({
          startU: currentU,
          endU: currentU + nodeU - 1,
          node,
        });
        currentU += nodeU;
        usedU += nodeU;
        power += node.specs.powerWatts + (node.specs.gpu?.tdpWatts || 0);
      }
    }

    // Fill remaining slots with empty
    while (currentU <= totalUnits) {
      result.push({
        startU: currentU,
        endU: currentU,
        node: null,
      });
      currentU++;
    }

    // Max power based on form factor
    const maxP = formFactor === 'rack-42' ? 10000 : formFactor === 'rack-18' ? 2000 : 1000;

    return { slots: result, usedUnits: usedU, totalPower: power, maxPower: maxP };
  }, [nodes, totalUnits, formFactor]);

  // Group consecutive empty slots
  const displaySlots = useMemo(() => {
    const grouped: RackSlot[] = [];
    let emptyStart: number | null = null;
    let emptyEnd: number | null = null;

    for (const slot of slots) {
      if (slot.node) {
        // If we have accumulated empty slots, push them first
        if (emptyStart !== null && emptyEnd !== null) {
          grouped.push({ startU: emptyStart, endU: emptyEnd, node: null });
          emptyStart = null;
          emptyEnd = null;
        }
        grouped.push(slot);
      } else {
        // Accumulate empty slots
        if (emptyStart === null) {
          emptyStart = slot.startU;
        }
        emptyEnd = slot.endU;
      }
    }

    // Don't forget trailing empty slots
    if (emptyStart !== null && emptyEnd !== null) {
      grouped.push({ startU: emptyStart, endU: emptyEnd, node: null });
    }

    return grouped;
  }, [slots]);

  // Get only the node slots for sortable context
  const nodeIds = nodes.map((n) => n.id);

  return (
    <div className="flex gap-6">
      {/* Rack visualization */}
      <div className="flex-1">
        <div className="rounded-lg border-2 border-gray-300 bg-gray-800 p-2">
          {/* Rack header */}
          <div className="mb-2 flex items-center justify-between border-b border-gray-600 pb-2">
            <span className="text-xs font-medium text-gray-400">
              {formFactor === 'rack-42' ? '42U Rack' : '18" Short-Depth Rack'}
            </span>
            <span className="text-xs text-gray-500">
              {usedUnits}U / {totalUnits}U used
            </span>
          </div>

          {/* Rack slots with drag-and-drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={nodeIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {displaySlots.map((slot) => {
                  const slotHeight = (slot.endU - slot.startU + 1) * PIXELS_PER_U;

                  if (!slot.node) {
                    // Empty slot(s)
                    return (
                      <div
                        key={`empty-${slot.startU}`}
                        className="flex items-center justify-center rounded border border-dashed border-gray-600 bg-gray-700/50"
                        style={{ height: slotHeight }}
                      >
                        <span className="text-xs text-gray-500">
                          {slot.endU - slot.startU + 1}U empty
                        </span>
                      </div>
                    );
                  }

                  return (
                    <DraggableRackNode
                      key={slot.node.id}
                      node={slot.node}
                      slotHeight={slotHeight}
                      isSelected={slot.node.id === selectedNodeId}
                      onNodeClick={onNodeClick}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Stats sidebar */}
      <div className="w-48 space-y-4">
        {/* Space usage */}
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Rack Space</h4>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-2xl font-bold text-gray-900">{usedUnits}</span>
            <span className="text-sm text-gray-500">/ {totalUnits}U</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                usedUnits / totalUnits > 0.9 ? 'bg-red-500' :
                usedUnits / totalUnits > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${(usedUnits / totalUnits) * 100}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {totalUnits - usedUnits}U available
          </p>
        </div>

        {/* Power budget */}
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h4 className="mb-2 flex items-center gap-1 text-xs font-medium uppercase text-gray-500">
            <Zap className="h-3 w-3" />
            Power Budget
          </h4>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-2xl font-bold text-gray-900">{totalPower}</span>
            <span className="text-sm text-gray-500">/ {maxPower}W</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                totalPower / maxPower > 0.9 ? 'bg-red-500' :
                totalPower / maxPower > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${Math.min((totalPower / maxPower) * 100, 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {maxPower - totalPower}W headroom
          </p>
        </div>

        {/* Node count by type */}
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Nodes</h4>
          <div className="space-y-1">
            {(['gateway', 'compute', 'storage', 'daq'] as NodeType[]).map((type) => {
              const count = nodes.filter((n) => n.type === type).length;
              const Icon = NODE_ICONS[type];
              const colors = NODE_COLORS[type];
              return (
                <div key={type} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <Icon className={clsx('h-3.5 w-3.5', colors.text)} />
                    <span className="capitalize text-gray-600">{type}</span>
                  </div>
                  <span className="font-medium text-gray-900">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
